import { HttpClient, HttpContext, HttpContextToken } from '@angular/common/http';
import { inject, Injectable, OnDestroy, signal, Signal } from '@angular/core';
import { BehaviorSubject, finalize, from, Observable, Subject, throwError } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { TokenService } from '../token-service/token.service';
import { BooleanStorageKeys, StorageKeys, StorageService, StorageType } from '../storage-service/storage.service';
import { RouterService } from '../router-service/router.service';
import {
    AccessTokenDecodedData,
    AccessTokenDetails,
    API_PATHS_TOKEN,
    BASE_URL_TOKEN,
    CLIENT_TYPE_TOKEN,
    ClientType,
    CodeLoginData,
    CodeLoginRequest,
    LaunchSource,
    LoginRequest,
    PRODUCT_DEFAULT_COUNTRY_CODE,
    RefreshTokenRequest,
    StaffLoginRequest,
    StaffRefreshTokenRequest,
    UserLoginData,
    WINDOW_TOKEN,
} from '@raptor-fe/core';
import { jwtDecode as jwt_decode } from 'jwt-decode';
import { NamedRoutes } from '../router-service/route.constants';
import { ApplicationContextStore } from '../application-context/application-context.store';
import { URLService } from '../url-service/url.service';
import { PushNotificationService } from '../push-notification-service/push-notification.service';
import { BrandingService } from '../branding-service/branding.service';
import { ServiceUtils } from '../service-utils/service-utils';
import { MsalAuthService } from '../msal-auth-service/msal-auth.service';

export const DISABLE_AUTH_HEADER = new HttpContextToken<boolean>(() => false);

/** Mirrors the API's AuthSources.Entra. */
export const STAFF_AUTH_SOURCE = 'entra';

/**
 * Launch sources whose host owns sign-out: the host closes or clears its embedded browser when its own
 * user signs out, so Raptor offers no Logout of its own. A launched session from a host not listed here
 * keeps Logout as normal.
 */
export const HOST_MANAGED_SIGN_OUT_SOURCES: ReadonlySet<LaunchSource> = new Set([LaunchSource.BlueDashboard]);

/**
 * Why a session is ending, which decides where the visitor goes next. Only a refused renewal may
 * re-launch: a user who signs out on purpose must not be signed straight back in. A credential the
 * server refuses on a session that holds a refresh token counts as a refused renewal, because the
 * session could have renewed and the host may still be able to launch it again.
 */
export enum SessionEndReason {
    SignedOut = 'signed-out',
    RenewalRefused = 'renewal-refused',
}

/** The scheme a server-issued launch url must use. */
const SECURE_LAUNCH_PROTOCOL = 'https:';

/** Plain http is accepted only for a loopback host, which is local development. */
const LOOPBACK_LAUNCH_PROTOCOL = 'http:';
const LOOPBACK_LAUNCH_HOSTS: ReadonlySet<string> = new Set(['localhost', '127.0.0.1']);

/** Session storage key holding when this tab last re-launched, in epoch milliseconds. */
export const LAST_RELAUNCH_AT_KEY = 'raptor.lastRelaunchAt';

/**
 * How soon after one re-launch another is refused. A launch that keeps yielding a session whose
 * renewal is refused would otherwise bounce the tab between Raptor and the host forever. Far below
 * the two-hour access token lifetime, so an ordinary expiry always re-launches.
 */
export const RELAUNCH_COOLDOWN_MS = 5 * 60 * 1000;

/**
 * Whether a launch url from the token is safe to send the window to. The claim comes from the
 * server, but a scheme such as javascript: or an http url off the loopback must never be followed
 * even if one were issued.
 */
function isSafeLaunchUrl(candidate: string): boolean {
    let parsed: URL;

    try {
        parsed = new URL(candidate);
    } catch {
        return false;
    }

    if (parsed.protocol === SECURE_LAUNCH_PROTOCOL) {
        return true;
    }

    if (parsed.protocol === LOOPBACK_LAUNCH_PROTOCOL && LOOPBACK_LAUNCH_HOSTS.has(parsed.hostname)) {
        return true;
    }

    return false;
}

/** Fixed Core API endpoints; constants rather than apiPaths entries, which never vary by environment. */
const STAFF_LOGIN_PATH = 'v1/authentication/staff-login';
const STAFF_REFRESH_PATH = 'v1/authentication/staff-token/refresh';

@Injectable({ providedIn: 'root' })
export class AuthService implements OnDestroy {
    redirectUrl = '';
    loggedOut = new BehaviorSubject(false);
    private isRefreshing = false;
    private refreshTokenSubject: BehaviorSubject<AccessTokenDetails | undefined> = new BehaviorSubject<AccessTokenDetails | undefined>(undefined);

    private lockSubject = new Subject<void>();

    /** The customer link verified in this tab. */
    private verifiedCustomerLinkId: string | null = null;

    /**
     * The sign-out in progress, so concurrent callers share it. Several requests can be refused at
     * once, and a second sign-out starting mid-way would find the token already cleared and send the
     * user to the sign-in, overriding the re-launch the first one chose.
     */
    private sessionEnding: Promise<void> | null = null;

    private readonly http = inject(HttpClient);
    private readonly browserWindow = inject(WINDOW_TOKEN);
    private readonly tokenService = inject(TokenService);
    //private readonly plt = inject(Platform);
    private readonly applicationContextStore = inject(ApplicationContextStore);
    private readonly storage = inject(StorageService);
    private readonly routerService = inject(RouterService);
    private readonly base_url = inject(BASE_URL_TOKEN);
    private readonly apiPaths = inject(API_PATHS_TOKEN);
    private readonly clientType = inject(CLIENT_TYPE_TOKEN, { optional: true }) ?? ClientType.MOBILE;
    private readonly urlService = inject(URLService);
    private readonly pushNotificationService = inject(PushNotificationService);
    private readonly brandingService = inject(BrandingService);
    private readonly serviceUtils = inject(ServiceUtils);
    private readonly productDefaultCountry = inject(PRODUCT_DEFAULT_COUNTRY_CODE, { optional: true }) ?? null;
    private readonly msalAuthService = inject(MsalAuthService, { optional: true });

    /**
     * Seeded from the stored token, so a customer tab that is reloaded is still known to be one: the application
     * context then reports it signed in from the token alone, and may hold a staff user's id and branch settings left
     * in local storage by an earlier staff session in the same browser.
     */
    private readonly customerSessionState = signal(this.isCustomerToken(this.tokenService.getToken()));

    /**
     * Whether this tab holds a customer session: one opened by a successful code login (a shared report or self
     * check-in link) rather than a staff sign-in. Set by a code login, cleared by a refused code login, a staff
     * sign-in, a sign-out, or a customer session sent back to verify its link. Carries no identity: a customer is
     * anonymous to everything that reads it.
     */
    readonly customerSession: Signal<boolean> = this.customerSessionState.asReadonly();

    // constructor() {
    //this.plt.ready().then(() => {
    //this.subscribeToPlatformResume();
    //});
    //}

    ngOnDestroy(): void {
        this.completeLockSubject();
    }

    /** Authentication */

    getDecodedAccessToken(token: string): AccessTokenDecodedData | null {
        try {
            if (token != null && token.length > 0) {
                return jwt_decode(token);
            }
            return null;
        } catch (e) {
            console.error(e);
            return null;
        }
    }

    /**
     * Logs in the user with the provided login data to the specified server URL.
     *
     * @param loginData - The user login data containing username and password
     * @param serverUrl - The URL of the server to log in to
     * @return An Observable that emits an AccessTokenDetails object upon successful login
     */
    login(loginData: UserLoginData, serverUrl: string, deviceId?: string): Observable<AccessTokenDetails> {
        const body: LoginRequest = {
            url: serverUrl,
            username: loginData.username,
            password: loginData.password,
            client_type: this.clientType,
            ...(deviceId ? { device_id: deviceId } : {}),
        };
        return this.http
            .post<AccessTokenDetails>(`${this.base_url}${this.apiPaths['login']}`, body, {
                context: new HttpContext().set(DISABLE_AUTH_HEADER, true),
            })
            .pipe(
                tap(async (res) => {
                    if (res?.access_token) {
                        await this.hydrateSession(res, !serverUrl);
                    } else {
                        this.applicationContextStore.setUsername('');
                        this.applicationContextStore.setDisplayName(res?.display_name ?? '');
                        this.tokenService.saveToken('token');
                        this.tokenService.saveRefreshToken('refresh_token');
                    }
                    this.verifiedCustomerLinkId = null;
                    this.customerSessionState.set(false);
                    this.applicationContextStore.setIsLoggedIn(true);
                }),
            );
    }

    /**
     * Exchanges a validated Entra token for a Raptor session. The Raptor bearer is suppressed
     * because BaseController rejects a stale one before the staff service ever runs.
     */
    staffLogin(staffToken: string): Observable<AccessTokenDetails> {
        const body: StaffLoginRequest = {
            staff_token: staffToken,
            client_type: this.clientType,
        };
        return this.http
            .post<AccessTokenDetails>(`${this.base_url}${STAFF_LOGIN_PATH}`, body, {
                context: new HttpContext().set(DISABLE_AUTH_HEADER, true),
            })
            .pipe(
                tap(async (res) => {
                    if (res?.access_token) {
                        this.tokenService.setStorageType(StorageType.SESSION);
                        this.urlService.removeURL();
                        await this.hydrateSession(res, true);
                        this.verifiedCustomerLinkId = null;
                        this.customerSessionState.set(false);
                        this.applicationContextStore.setIsLoggedIn(true);
                    }
                }),
            );
    }

    /**
     * Renewal re-presents the Entra token because the API re-validates it, so app-role removal
     * refuses the next refresh without any revocation machinery of ours. The storage type is set
     * again rather than inherited from sign-in, because the application context resets it to the
     * per-application-mode default on every page load. The directory acquisition sits inside the
     * in-flight guard so concurrent 401s collapse onto one renewal rather than one acquisition each.
     */
    staffRefreshToken(refreshToken: string): Observable<AccessTokenDetails | undefined> {
        if (this.isRefreshing) {
            return this.refreshTokenSubject.asObservable();
        }

        const msalAuthService = this.msalAuthService;

        if (!msalAuthService) {
            return throwError(() => new Error('Staff renewal requires the directory client.'));
        }

        this.isRefreshing = true;
        this.refreshTokenSubject.next(undefined);

        return from(msalAuthService.acquireToken(this.staffDirectoryObjectId())).pipe(
            switchMap((staffToken) => {
                const body: StaffRefreshTokenRequest = {
                    staff_token: staffToken,
                    refresh_token: refreshToken,
                    client_type: this.clientType,
                };

                return this.http.post<AccessTokenDetails>(`${this.base_url}${STAFF_REFRESH_PATH}`, body, {
                    context: new HttpContext().set(DISABLE_AUTH_HEADER, true),
                });
            }),
            tap((res) => {
                if (!res?.access_token) {
                    throw new Error('The staff renewal response carried no access token.');
                }

                this.tokenService.setStorageType(StorageType.SESSION);
                this.tokenService.removeToken();
                this.tokenService.removeRefreshToken();

                this.tokenService.saveToken(res.access_token);
                this.tokenService.saveRefreshToken(res.refresh_token ?? '');
                this.refreshTokenSubject.next(res);
            }),
            finalize(() => {
                this.isRefreshing = false;
            }),
        );
    }

    /**
     * Whether the current session was established through staff sign-in. The claim is minted by
     * the API from a trusted argument and cannot be supplied by a client.
     */
    isStaffSession(): boolean {
        const token = this.tokenService.getToken();
        return this.getDecodedAccessToken(token ?? '')?.auth_source === STAFF_AUTH_SOURCE;
    }

    /**
     * Whether the host application that opened this session owns sign-out, so Raptor should offer no
     * Logout of its own. Only sources listed in HOST_MANAGED_SIGN_OUT_SOURCES qualify: a session
     * launched by any other host, including one this build does not know, keeps Logout. The claim is
     * minted by the federation API and cannot be supplied by a client.
     */
    isSignOutManagedByHost(): boolean {
        const token = this.tokenService.getToken();

        if (!token) {
            return false;
        }

        const launchSource = this.getDecodedAccessToken(token)?.launch_source;

        if (!launchSource) {
            return false;
        }

        return HOST_MANAGED_SIGN_OUT_SOURCES.has(launchSource);
    }

    /**
     * The directory object id the staff session belongs to, so renewal can be bound to that identity
     * rather than to whichever account the local Entra cache lists first.
     */
    staffDirectoryObjectId(): string | undefined {
        const token = this.tokenService.getToken();
        return this.getDecodedAccessToken(token ?? '')?.external_object_id;
    }

    refreshToken(refreshToken: string): Observable<AccessTokenDetails | undefined> {
        if (this.isRefreshing) {
            return this.refreshTokenSubject.asObservable();
        }

        this.isRefreshing = true;
        this.refreshTokenSubject.next(undefined); // Clear previous token

        const expiredToken = this.tokenService.getToken();

        const body: RefreshTokenRequest = {
            access_token: expiredToken ?? '',
            refresh_token: refreshToken,
            client_type: this.clientType,
        };
        return this.http
            .post<AccessTokenDetails>(`${this.base_url}${this.apiPaths['refreshToken']}`, body, {
                context: new HttpContext().set(DISABLE_AUTH_HEADER, true),
            })
            .pipe(
                tap((res) => {
                    this.tokenService.removeToken();
                    this.tokenService.removeRefreshToken();

                    this.tokenService.saveToken(res?.access_token ?? '');
                    this.tokenService.saveRefreshToken(res.refresh_token ?? '');
                    this.refreshTokenSubject.next(res);
                }),
                finalize(() => {
                    this.isRefreshing = false;
                }),
            );
    }

    /**
     * Ends the session and returns to the sign-in the visitor can actually use. A session opened
     * from a customer link goes back to that link's verification page: the customer has no staff
     * credentials, so the staff sign-in is a dead end for them. A launched session whose renewal was
     * refused goes back through the launch url it came through instead: the host either still holds a
     * live session and signs the user straight back in, or reports why it cannot.
     *
     * @param reason - Why the session is ending. Defaults to a deliberate sign-out. A call made while
     * a sign-out is already in progress joins it, whatever its reason.
     */
    logout(reason: SessionEndReason = SessionEndReason.SignedOut): Promise<void> {
        if (this.sessionEnding) {
            return this.sessionEnding;
        }

        const ending = this.endSession(reason).catch((error: unknown) => {
            this.sessionEnding = null;
            throw error;
        });

        this.sessionEnding = ending;
        return ending;
    }

    /**
     * Cleared only on failure, so a failed sign-out can be retried. A successful one leaves the page,
     * so the in-flight promise never needs resetting.
     */
    private async endSession(reason: SessionEndReason): Promise<void> {
        if (this.msalAuthService?.isConfigured() && this.isStaffSession()) {
            await this.msalAuthService.signOut().catch((error) => console.error('Could not clear the Entra cache on sign-out.', error));
        }

        const endingSession = this.getDecodedAccessToken(this.tokenService.getToken() ?? '');
        const signIn = endingSession?.access_code_type ? NamedRoutes.codeLogin(endingSession.access_code_id) : NamedRoutes.login();
        const relaunchUrl = this.relaunchUrlFor(reason);

        this.verifiedCustomerLinkId = null;
        this.customerSessionState.set(false);
        this.applicationContextStore.setBranding(null);
        this.tokenService.removeToken();
        this.tokenService.removeRefreshToken();
        this.applicationContextStore.setUsername('');
        this.loggedOut.next(true);
        await Promise.all([
            this.storage.removeItem(StorageKeys.SELECTED_BRANCH),
            this.storage.removeItem(BooleanStorageKeys.IS_SINGLE_BRANCH_USER),
            this.storage.removeItem(StorageKeys.ROLES),
            this.storage.removeItem(BooleanStorageKeys.IS_WEB_VIEW_MODE),
            this.storage.removeItem(StorageKeys.BACKEND_TYPE),
            this.storage.removeItem(BooleanStorageKeys.IS_WORKDAY_CLOCKED_IN),
            this.storage.removeItem(StorageKeys.FEATURES),
            this.storage.removeItem(StorageKeys.PERMISSIONS),
            this.storage.removeItem(StorageKeys.COUNTRY_CODE),
            this.applicationContextStore.reset(),
        ]);

        if (relaunchUrl) {
            await this.relaunch(relaunchUrl);
            return;
        }

        await this.routerService.navigateAndReplaceUrl(signIn);
        this.browserWindow.location.reload();
    }

    /**
     * The launch url to leave through, or null to use the ordinary sign-in. Read before the token is
     * cleared, because the launch url is a claim on the ending session.
     */
    private relaunchUrlFor(reason: SessionEndReason): string | null {
        if (reason !== SessionEndReason.RenewalRefused) {
            return null;
        }

        const launchUrl = this.getDecodedAccessToken(this.tokenService.getToken() ?? '')?.launch_url;

        if (!launchUrl) {
            return null;
        }

        if (!isSafeLaunchUrl(launchUrl)) {
            return null;
        }

        return launchUrl;
    }

    /**
     * Leaves through the launch url unless this tab re-launched within the cooldown. A repeat that
     * soon means the launch is not producing a usable session, so the launch page is shown instead:
     * with no code and no token it explains the failure rather than looping.
     */
    private async relaunch(launchUrl: string): Promise<void> {
        if (this.relaunchedRecently()) {
            await this.routerService.navigateAndReplaceUrl(NamedRoutes.launch());
            this.browserWindow.location.reload();
            return;
        }

        this.recordRelaunch();
        this.browserWindow.location.href = launchUrl;
    }

    /**
     * Storage that cannot be read counts as a recent re-launch. Without it no cooldown could ever be
     * recorded, so re-launching would risk the very loop the cooldown exists to stop.
     */
    private relaunchedRecently(): boolean {
        let recorded: string | null;

        try {
            recorded = this.browserWindow.sessionStorage.getItem(LAST_RELAUNCH_AT_KEY);
        } catch (error) {
            console.error('Could not read the last re-launch time.', error);
            return true;
        }

        if (!recorded) {
            return false;
        }

        const lastRelaunchAt = Number(recorded);

        if (!Number.isFinite(lastRelaunchAt)) {
            return false;
        }

        return Date.now() - lastRelaunchAt < RELAUNCH_COOLDOWN_MS;
    }

    private recordRelaunch(): void {
        try {
            this.browserWindow.sessionStorage.setItem(LAST_RELAUNCH_AT_KEY, String(Date.now()));
        } catch (error) {
            console.error('Could not record the re-launch time.', error);
        }
    }

    /**
     * The customer link this tab verified, even if the token has since been lost. Null for staff.
     */
    customerLinkId(): string | null {
        return this.verifiedCustomerLinkId ?? this.getDecodedAccessToken(this.tokenService.getToken() ?? '')?.access_code_id ?? null;
    }

    /**
     * Sends a customer whose session has ended back to verify their link.
     * Reads localStorage directly because the storage service only reads sessionStorage for customers.
     * The application context is marked signed out too: a reloaded customer tab reports signed-in from its token
     * alone, and once the token is gone nothing else would correct it.
     */
    async returnToCustomerLink(): Promise<void> {
        const accessCodeId = this.customerLinkId() ?? this.browserWindow.localStorage?.getItem(StorageKeys.ACCESS_CODE_ID) ?? undefined;
        this.tokenService.removeToken();
        this.tokenService.removeRefreshToken();
        this.customerSessionState.set(false);
        this.applicationContextStore.setIsLoggedIn(false);
        await this.routerService.navigateAndReplaceUrl(NamedRoutes.codeLogin(accessCodeId));
    }

    isLoggedIn(): boolean {
        return this.applicationContextStore.isLoggedIn();
    }

    /**
     * Whether a password or staff renewal is in flight. A refusal arriving meanwhile must wait for it
     * rather than end the session, because ending the session reloads the page and cancels the renewal.
     */
    isRenewing(): boolean {
        return this.isRefreshing;
    }

    /**
     * Exchanges a customer link's credential for a customer session. A refusal ends any customer session this tab
     * held, because both callers clear the previous token before trying.
     */
    codeLogin(codeLoginData: CodeLoginData): Observable<AccessTokenDetails> {
        const body: CodeLoginRequest = {
            code: codeLoginData.code,
            id: codeLoginData.id,
            registration: codeLoginData.registration,
        };
        return this.http
            .post<AccessTokenDetails>(`${this.base_url}v1/authentication/code-login`, body, {
                context: new HttpContext().set(DISABLE_AUTH_HEADER, true),
            })
            .pipe(
                tap(async (res) => {
                    if (res?.access_token) {
                        const openToken = this.getDecodedAccessToken(res.access_token);

                        this.urlService.saveURL(openToken?.external_url as string);

                        this.verifiedCustomerLinkId = openToken?.access_code_id ?? codeLoginData.id ?? null;
                        this.tokenService.saveToken(res.access_token);
                        this.tokenService.saveRefreshToken(res.refresh_token ?? '');
                        this.applicationContextStore.setTenantId(openToken?.tenant_id ?? '');
                        this.applicationContextStore.setBranch({
                            id: openToken?.branch_id ?? '',
                            name: '',
                        });
                        this.customerSessionState.set(true);
                        await this.applicationContextStore.setRoles(openToken?.role ?? []);
                        this.fetchAndApplyBranding(openToken?.tenant_id ?? '', openToken?.branch_id ?? '');
                    } else {
                        this.customerSessionState.set(false);
                        this.tokenService.saveToken('token');
                        this.tokenService.saveRefreshToken('refresh_token');
                    }
                }),
                tap({ error: () => this.customerSessionState.set(false) }),
            );
    }

    /** Password Management */

    forgotPassword(data: { email: string }): Observable<void> {
        return this.http.post<void>(`${this.serviceUtils.getBaseUrl('authentication')}/forgot-password`, data, {
            context: new HttpContext().set(DISABLE_AUTH_HEADER, true),
        });
    }

    resetPassword(data: { token: string; email: string; new_password: string; confirm_password: string }): Observable<void> {
        return this.http.post<void>(`${this.serviceUtils.getBaseUrl('authentication')}/reset-password`, data, {
            context: new HttpContext().set(DISABLE_AUTH_HEADER, true),
        });
    }

    changePassword(data: { current_password: string; new_password: string; confirm_password: string }): Observable<void> {
        return this.http.post<void>(`${this.serviceUtils.getBaseUrl('authentication')}/change-password`, data);
    }

    adminResetPassword(tenantId: string, userId: string): Observable<void> {
        return this.http.post<void>(`${this.serviceUtils.getBaseUrl('user', tenantId)}/${userId}/reset-password`, {});
    }

    /**
     * Applies a freshly issued Raptor session to storage and the application context. Shared by
     * the password and staff paths so the two cannot drift.
     */
    private async hydrateSession(res: AccessTokenDetails, isStandaloneTenant: boolean): Promise<void> {
        const openToken = this.getDecodedAccessToken(res.access_token ?? '');

        this.pushNotificationService.initialize();

        this.tokenService.saveToken(res.access_token ?? '');
        this.tokenService.saveRefreshToken(res.refresh_token ?? '');

        this.applicationContextStore.setUsername(openToken?.unique_name ?? '');
        this.applicationContextStore.setDisplayName(openToken?.sub ?? '');
        this.applicationContextStore.setTenantId(openToken?.tenant_id ?? '');
        this.applicationContextStore.setBranch({
            id: openToken?.branch_id ?? '',
            name: '',
        });
        await this.applicationContextStore.setUserId(openToken?.user_id ?? '');
        await this.applicationContextStore.setRoles(openToken?.role ?? []);
        await this.applicationContextStore.setIsStandaloneTenant(isStandaloneTenant);
        await this.applicationContextStore.setFeatures(openToken?.feature ?? []);

        this.fetchAndApplyBranding(openToken?.tenant_id ?? '', openToken?.branch_id ?? '');

        await this.persistCountryAndReloadIfChanged(openToken?.country_code);
    }

    /**
     * Persists the decoded country claim and reloads once when the effective locale
     * changes so the locale-derived providers are rebuilt at bootstrap. The effective
     * locale resolves the product default when the claim is absent, which prevents a
     * spurious reload on first login. Reading the stored value before overwriting
     * prevents a reload loop.
     */
    private async persistCountryAndReloadIfChanged(country: string | undefined): Promise<void> {
        const productDefault = (this.productDefaultCountry ?? '').trim().toUpperCase();
        const claimCountry = country?.trim().toUpperCase() ?? '';
        const storedCountry = await this.storage.getString(StorageKeys.COUNTRY_CODE);
        const previousCountry = storedCountry.value?.trim().toUpperCase() ?? '';

        await this.storage.setString(StorageKeys.COUNTRY_CODE, claimCountry);

        const effectiveClaim = claimCountry || productDefault;
        const effectivePrevious = previousCountry || productDefault;

        if (effectiveClaim !== effectivePrevious) {
            this.browserWindow.location.reload();
        }
    }

    /**
     * A customer token is one minted by a code login, which always carries the access code's type. A stored value that
     * is not a JWT (the placeholder a token-less login saves) is not decoded, so the check at start-up logs no error.
     */
    private isCustomerToken(token: string | null): boolean {
        if (!token || token.split('.').length !== 3) {
            return false;
        }
        return !!this.getDecodedAccessToken(token)?.access_code_type;
    }

    private fetchAndApplyBranding(tenantId: string, branchId: string): void {
        if (!tenantId || !branchId) {
            return;
        }
        this.brandingService.getEffectiveBranding(tenantId, branchId).subscribe({
            next: (branding) => this.applicationContextStore.setBranding(branding),
            error: () => this.applicationContextStore.setBranding(null),
        });
    }

    private completeLockSubject(): void {
        this.lockSubject.next();
        this.lockSubject.complete();
    }
}
